import React, { useCallback, useEffect, useRef, useState } from "react";
import Posts from "./Posts/Posts";
import { API } from "../api";
import Greetings from "./Greetings";

const POST_LIMIT = 3;

const FeedSkeleton = () => {
  return (
    <div className="w-full animate-pulse">
      <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 h-6 w-56 rounded-md bg-gray-200" />
        <div className="h-4 w-36 rounded-md bg-gray-200" />
      </div>

      {[1, 2, 3].map((item) => (
        <div key={item} className="mb-9 w-full rounded-md border bg-white p-4 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <div className="h-11 w-11 shrink-0 rounded-full bg-gray-200" />

            <div className="flex-1">
              <div className="mb-2 h-4 w-40 rounded bg-gray-200" />
              <div className="h-3 w-24 rounded bg-gray-200" />
            </div>
          </div>

          <div className="mb-3 h-4 w-full rounded bg-gray-200" />
          <div className="mb-6 h-4 w-3/4 rounded bg-gray-200" />

          <div className="flex items-center justify-between border-t pt-4">
            <div className="h-4 w-20 rounded bg-gray-200" />
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-4 w-20 rounded bg-gray-200" />
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
            <div className="h-10 flex-1 rounded-lg bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
};

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const loadMoreRef = useRef(null);

  /* =========================================================
     FETCH POSTS
  ========================================================= */

  const fetchPosts = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);

      const res = await API.get(`/posts?limit=${POST_LIMIT}&offset=${offsetRef.current}`);
      const newPosts = res.data || [];

      setPosts((prevPosts) => {
        const existingIds = new Set(prevPosts.map((post) => post.id));
        const uniqueNewPosts = newPosts.filter((post) => !existingIds.has(post.id));

        return [...prevPosts, ...uniqueNewPosts];
      });

      offsetRef.current += newPosts.length;

      if (newPosts.length < POST_LIMIT) {
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  /* =========================================================
     INITIAL POSTS
  ========================================================= */

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  /* =========================================================
     INFINITE SCROLL
  ========================================================= */

  useEffect(() => {
    const element = loadMoreRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingRef.current && hasMoreRef.current) {
          fetchPosts();
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [posts.length]);

  return (
  <div className="w-full">
    <div className="w-full">
      <div className="home-posts">
        <Greetings />
      </div>

      <div className="w-full home-posts">
        {posts.length !== 0 ? (
          <div className="w-full">
            <div className="home-posts">
              <Posts posts={posts} currentId={currentId} setCurrentId={setCurrentId} />
            </div>

            {loading && (
              <div className="flex w-full items-center justify-center py-6">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-purple-600" />
              </div>
            )}

            {!hasMore && (
              <div className="py-8 text-center">
                <p className="text-sm font-medium text-gray-400">You've reached the end.</p>
              </div>
            )}

            {hasMore && <div ref={loadMoreRef} className="h-px w-full" />}
          </div>
        ) : loading ? (
          <div className="w-full">
            <FeedSkeleton />
          </div>
        ) : (
          <div className="h-[calc(100vh-6rem)] w-full">
            <div className="flex h-full w-full flex-col items-center justify-center">
              <img src="/assets/icons/posts.svg" alt="no post" className="h-36 w-36 object-contain" />

              <p className="text-center text-xl font-semibold text-gray-400">
                No post to see yet!
                <br />
                Be the first person to post something to the world.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default Feed;